<template>
  <div 
    class="confirmation-container d-flex align-items-center justify-content-center"
    :class="{ 'compact-view': asistenciaConfirmada || showQRCode }"
  >
    <div 
      class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
      :class="{ 'compact-card': asistenciaConfirmada || showQRCode }"
      ref="formCardRef"
    >
      <!-- Vista de QR confirmado -->
      <div v-if="asistenciaConfirmada || showQRCode" class="qr-view text-center">
        <p class="qr-header">Pase Digital</p>
        <div class="qr-wrapper">
          <img v-if="qrCode" :src="qrCode" alt="Código QR" class="qr-code">
          <p v-else class="qr-error-message">No se ha generado el código QR</p>
        </div>
        <p class="qr-instructions">Preséntalo el día del evento</p>
      </div>

      <!-- Formulario paso a paso -->
      <div v-else>
        <div class="form-header">
          <h2 ref="tituloAnimado" class="form-title">
            Confirma<br><span class="subtitulo">Asistencia</span>
          </h2>
          <br>

          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
            ></div>
          </div>
          <p class="form-subtitle">Paso {{ currentStep }} de {{ totalSteps }}</p>
        </div>

        <form @submit.prevent="handleSubmit" class="confirmation-form">
          <!-- Paso 1: Nombre y Teléfono -->
          <div v-if="currentStep === 1" class="step-box">
            <div class="form-group">
              <label for="name" class="form-label">Nombre</label>
              <input
                id="name"
                v-model.trim="name"
                type="text"
                class="form-control"
                placeholder="Ingresa tu nombre completo"
                required
              >
            </div>
            <div class="form-group">
              <label for="phone" class="form-label">Teléfono</label>
              <input
                id="phone"
                v-model.trim="phone"
                type="tel"
                class="form-control"
                placeholder="Ingresa tu teléfono"
                required
                pattern="[0-9]{10}"
                title="Ingresa un número de 10 dígitos"
              >
            </div>
          </div>

          <!-- Paso 2: Invitado por (oculto en XV o YM) -->
          <div v-if="currentStep === 2 && !isXvEvent" class="step-box">
            <div class="form-group">
              <label class="form-label">¿Invitado por?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="novia" v-model="invitedBy" type="radio" value="novia" class="radio-input" required>
                  <label for="novia" class="radio-label">Novia</label>
                </div>
                <div class="radio-option">
                  <input id="novio" v-model="invitedBy" type="radio" value="novio" class="radio-input">
                  <label for="novio" class="radio-label">Novio</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Paso 3: Asistencia -->
          <div v-if="currentStep === asistenciaStep" class="step-box">
            <div class="form-group">
              <label class="form-label">¿Asistirás?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="si" v-model="attending" type="radio" value="si" class="radio-input" required>
                  <label for="si" class="radio-label">Sí</label>
                </div>
                <div class="radio-option">
                  <input id="no" v-model="attending" type="radio" value="no" class="radio-input">
                  <label for="no" class="radio-label">No</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Paso 4: Número de invitados -->
          <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
            <div class="form-group">
              <label for="guests" class="form-label">¿Cuántos asisten?</label>
              <select
                id="guests"
                v-model.number="guests"
                class="form-select"
                :disabled="attending === 'no' || loading"
                required
              >
                <option :value="null" disabled>Selecciona una opción</option>
                <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
              </select>
              <div v-if="loading" class="loading-indicator">Cargando opciones...</div>
            </div>
          </div>

          <!-- Botones de navegación -->
          <div class="d-flex justify-content-between mt-3">
            <button type="button" class="submit-button" @click="prevStep" v-if="currentStep > 1">Atrás</button>
            <button v-if="currentStep < totalSteps" type="button" class="submit-button" @click="nextStep">Siguiente</button>
            <button v-if="currentStep === totalSteps" type="submit" class="submit-button" :disabled="isSubmitting">
              <span v-if="!isSubmitting">Confirmar</span>
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </button>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
        </form>
      </div>
    </div>

    <div v-show="showTooltip" ref="tooltipRef" class="tooltip">
      ¡Recuerda confirmar asistencia!
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed, nextTick } from 'vue';
import axios from 'axios';
import baseUrl from '@/api';
import gsap from 'gsap';
import Swal from 'sweetalert2';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: { type: Number, required: true, validator: v => Number.isInteger(v) && v > 0 },
    nombre: { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup(props) {
    const name = ref('');
    const phone = ref('');
    const invitedBy = ref('');
    const attending = ref('');               // 'si' | 'no'
    const guests = ref(null);                // numérico o null
    const guestsOptions = ref([1, 2]);
    const loading = ref(true);
    const isSubmitting = ref(false);
    const errorMessage = ref('');

    const tituloAnimado = ref(null);

    const qrCode = ref(null);
    const showQRCode = ref(false);
    const asistenciaConfirmada = ref(false);
    const currentStep = ref(1);
    const formCardRef = ref(null);

    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase();
      return ['xv', 'ym'].includes(eventName);
    });

    const totalSteps = computed(() => isXvEvent.value ? 3 : 4);
    const asistenciaStep = computed(() => isXvEvent.value ? 2 : 3);

    const animateStep = () => {
      nextTick(() => {
        const box = formCardRef.value?.querySelector('.step-box');
        if (!box) return;
        gsap.fromTo(
          box,
          { y: 50, opacity: 0, transformPerspective: 300, rotationX: -10 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out', rotationX: 0 }
        );
      });
    };

    const nextStep = async () => {
      // Paso 1: Nombre y teléfono
      if (currentStep.value === 1) {
        if (!name.value.trim() || !phone.value.trim()) {
          errorMessage.value = 'Por favor completa tu nombre y teléfono.';
          return;
        }
      }

      // Paso 2 (si aplica): Invitado por
      if (currentStep.value === 2 && !isXvEvent.value) {
        if (!invitedBy.value) {
          errorMessage.value = 'Selecciona quién te invitó.';
          return;
        }
      }

      // Paso de Asistencia
      if (currentStep.value === asistenciaStep.value) {
        if (!attending.value) {
          errorMessage.value = 'Por favor selecciona si asistirás o no.';
          return;
        }
        // Si dijo que no asiste, saltar a último paso y enviar
        if (attending.value === 'no') {
          currentStep.value = totalSteps.value;
          await nextTick();
          handleSubmit();
          return;
        }
      }

      // Último paso (si asiste): cuántos asisten
      if (currentStep.value === totalSteps.value && attending.value === 'si') {
        if (guests.value == null || Number.isNaN(Number(guests.value))) {
          errorMessage.value = 'Por favor selecciona cuántas personas asistirán.';
          return;
        }
      }

      // OK -> avanzar
      errorMessage.value = '';
      if (currentStep.value < totalSteps.value) {
        currentStep.value++;
        animateStep();
      }
    };

    const prevStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--;
        animateStep();
      }
    };

    const fetchGuestLimit = async () => {
      try {
        const response = await axios.get(`${baseUrl}/${props.nombre ? `${props.nombre}/` : ''}${props.idFestejado ? `${props.idFestejado}/` : ''}${props.idInvitado}`);
        const numPases = Number(response.data.num_pase);
        guestsOptions.value = numPases > 0
          ? Array.from({ length: numPases }, (_, i) => i + 1)
          : [1, 2];
      } catch (error) {
        console.error('Error al obtener límite de invitados:', error);
        errorMessage.value = 'Error al cargar las opciones de invitados';
      } finally {
        loading.value = false;
      }
    };

    const verificarAsistencia = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/verificar-asistencia`, {
          idInvitado: props.idInvitado
        });
        if (response.data.asistio) {
          asistenciaConfirmada.value = true;
          await obtenerCodigoQR();
        }
      } catch (error) {
        console.error('Error al verificar asistencia:', error);
      }
    };

    const obtenerCodigoQR = async () => {
      try {
        const response = await axios.post(`${baseUrl}/api2/codigoqrinvitado`, { id_invitado: Number(props.idInvitado) });
        qrCode.value = response.data?.codigoqr || null;
      } catch (error) {
        console.error('Error obteniendo código QR:', error);
        errorMessage.value = 'Error al generar el código QR';
      }
    };

    const handleSubmit = async () => {
      if (attending.value === 'no') guests.value = 0;

      isSubmitting.value = true;
      errorMessage.value = '';

      try {
        const formData = {
          nombre_asistencia: name.value.trim(),
          telefono_invitado: phone.value.trim(),
          invitedby: isXvEvent.value ? 'novia' : invitedBy.value,
          asistira: attending.value,                      // 'si' | 'no'
          id_festejada: props.idFestejado,
          id_invitado: props.idInvitado,
          num_pases: Number(attending.value === 'no' ? 0 : (guests.value ?? 0)), // <-- FIX
        };

        await axios.post(`${baseUrl}/asistencia`, formData);

        if (attending.value === 'no') {
          Swal.fire({
            icon: 'info',
            title: '¡Gracias por avisarnos!',
            text: 'Sentimos mucho que no puedas asistir.',
            confirmButtonColor: '#d88cbd',
          });
          asistenciaConfirmada.value = true; // Oculta el formulario
        } else {
          await obtenerCodigoQR();
          showQRCode.value = true;
        }
      } catch (error) {
        console.error('Error confirmando asistencia:', error);
        errorMessage.value = 'Hubo un error al confirmar. Por favor intenta nuevamente.';
      } finally {
        isSubmitting.value = false;
      }
    };

    // Si cambia asistencia, ajusta guests numéricamente
    watch(attending, (newValue) => {
      if (newValue === 'no') {
        guests.value = 0;
      } else if (newValue === 'si' && (guests.value === 0 || guests.value == null)) {
        guests.value = 1;
      }
    });

    onMounted(() => {
      fetchGuestLimit();
      verificarAsistencia();
      animateStep();

      gsap.fromTo(
        tituloAnimado.value,
        { y: -150, opacity: 0, scale: 1.2 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: 'bounce.out',
          scrollTrigger: {
            trigger: tituloAnimado.value,
            start: 'top 80%',
            toggleActions: 'restart none none reset'
          }
        }
      );
    });

    return {
      name, phone, invitedBy, attending, guests, guestsOptions,
      loading, isSubmitting, errorMessage, isXvEvent, asistenciaConfirmada,
      qrCode, showQRCode, currentStep, nextStep, prevStep, handleSubmit,
      totalSteps, asistenciaStep, formCardRef, tituloAnimado
    };
  }
};
</script>

<style scoped>
/* Estilos base */
.confirmation-container {
  min-height: 70vh;
  padding: 2rem 1rem;
  transition: all 0.3s ease;
}
.confirmation-card {
  width: 100%;
  max-width: 500px;
  background: white;
  background: linear-gradient(to bottom, #ffffff, #f9f9f9);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}
/* Vista compacta para mensaje de confirmación */
.compact-view {
  min-height: 70vh;
  align-items: flex-start;
  padding-top: 4rem;
}
.compact-card {
  max-width: 400px;
  padding: 2rem;
}
/* Encabezado del formulario */
.form-header {
  margin-bottom: 2rem;
  text-align: center;
}
.form-title {
  font-family: 'Playfair Display', serif;
  font-weight: 400;
  font-size: 32px !important;
  color: #e1b4b9;
  margin-bottom: 0.5rem;
}
.form-subtitle {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 1rem;
}
/* Grupos de formulario */
.form-group {
  margin-bottom: 1.5rem;
}
.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: 'Karla', sans-serif;
  font-weight: 500;
  color: #495057;
}
/* Controles de formulario */
.form-control,
.form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background-color: rgba(255, 255, 255, 0.8);
  transition: border-color 0.3s, box-shadow 0.3s;
}
.form-control:focus,
.form-select:focus {
  border-color: #e1b4b9;
  box-shadow: 0 0 0 0.25rem rgba(213, 146, 176, 0.25);
  outline: none;
}
/* Radio buttons */
.radio-group {
  display: flex;
  gap: 1.5rem;
}
.radio-option {
  display: flex;
  align-items: center;
}
.radio-input {
  margin-right: 0.5rem;
  accent-color: #e1b4b9;
}
.radio-label {
  font-family: 'Karla', sans-serif;
  color: #495057;
}
/* Botón de enviar */
.submit-button {
  width: 100%;
  padding: 0.75rem;
  font-family: 'FeelingPassionate', cursive;
  font-size: 1.25rem;
  color: rgb(249, 249, 249);
  background-color: #e1b4b9;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
}
.submit-button:hover {
  background-color: #e1b4b9;
}
.submit-button:disabled {
  background-color: #e1b4b9;
  opacity: 0.7;
  cursor: not-allowed;
}
/* Vista mensaje final */
.qr-view {
  padding: 1rem;
}
.confirmation-thanks {
  font-family: 'Karla', sans-serif;
  font-weight: 600;
  color: #354257;
  font-size: 1.25rem;
  margin-bottom: 0;
}
/* Mensajes de error */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}
.loading-indicator {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.5rem;
}
/* Responsive */
@media (max-width: 576px) {
  .confirmation-card {
    padding: 1.5rem;
  }
  
  .form-title {
    font-size: 1.75rem;
  }
  
  .radio-group {
    gap: 1rem;
  }
}
.progress-container {
  height: 8px;
  margin-top: 40px;
  background-color: #e0e0e0;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden;
}
.progress-bar {
  height: 100%;
  background-color: #83808b;
  transition: width 0.4s ease;
}
</style>